import React, { useState, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Button,
  Box,
  Typography,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
  Icon,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import CampoTelefonoConCodigo from './CampoTelefonoConCodigo';
import { desencriptar } from '../utilities/encriptado';
import AlertaContext from '../context/AlertaContext';

const validationSchema = Yup.object().shape({
  nombre: Yup.string()
    .required('El nombre es obligatorio')
    .min(3, 'El nombre debe tener al menos 3 caracteres'),
  email: Yup.string()
    .email('Email inválido')
    .nullable(),
  telefono: Yup.string()
    .required('El teléfono es obligatorio')
    .min(7, 'El teléfono debe tener al menos 7 dígitos'),
});

const FormularioReserva = ({
  open,
  onClose,
  numerosSeleccionados = [],
  rifa = {},
  onSuccess
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { handleOpenAlerta } = useContext(AlertaContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const initialValues = {
    nombre: '',
    email: '',
    telefono: '',
    codigo_pais: '',
  };

  const handleRemoveNumber = (numeroToRemove) => {
    if (onSuccess) {
      onSuccess({
        action: 'removeNumber',
        numero: numeroToRemove
      });
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    setError(null);

    try {
      const codigoPrefijo = selectedCountry?.prefijo || selectedCountry?.codigo_telefono || values.codigo_pais;
      const codigoPais = codigoPrefijo
        ? (String(codigoPrefijo).startsWith('+')
          ? String(codigoPrefijo)
          : `+${codigoPrefijo}`)
        : '+57';

      const payload = {
        nombre: values.nombre,
        codigo: codigoPais,
        telefono: values.telefono,
        email: values.email || null,
        numeros: numerosSeleccionados,
        estado: '2',
      };

      const apiUrl = process.env.REACT_APP_URL || 'https://apitest.rauhtech.com/v2/misrifas';
      const response = await axios.put(`${apiUrl}/rifas/reserva/${rifa.id}`, payload);

      setSuccess(true);
      handleClose();

      if (onSuccess) {
        onSuccess({
          action: 'success',
          data: response.data
        });
      }

      const boletosTexto = numerosSeleccionados.length === 1
        ? `El boleto #${numerosSeleccionados[0]} ha sido reservado exitosamente.`
        : `Tus ${numerosSeleccionados.length} boletos (${numerosSeleccionados.join(', ')}) han sido reservados exitosamente.`;

      handleOpenAlerta('success', response.data?.message || boletosTexto);

    } catch (error) {
      console.error('Error al procesar la reserva:', error);
      let errorMessage = 'Error al procesar la reserva. Por favor intenta de nuevo.';

      if (error.response?.data) {
        let errData = error.response.data;
        if (typeof errData === 'string') {
          try {
            const desc = desencriptar(errData);
            if (desc) errData = desc;
          } catch (decryptError) { }
        }
        if (errData?.message) errorMessage = errData.message;
        if (errData?.data?.numerosNoDisponibles && onSuccess) {
          onSuccess({
            action: 'removeUnavailableNumbers',
            numerosNoDisponibles: errData.data.numerosNoDisponibles,
          });
        }
      }

      setError(errorMessage);
      handleOpenAlerta('error', errorMessage);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setError(null);
    setSuccess(false);
    setSelectedCountry(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const totalPagar = (numerosSeleccionados.length * parseFloat(rifa?.precio || 0)).toLocaleString('es-CO');

  return (
    <Dialog
      fullScreen={isMobile}
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '12px',
          maxHeight: isMobile ? '100%' : '90vh',
          height: isMobile ? '100%' : 'auto',
          overflow: 'hidden',
          bgcolor: theme.palette.mode === 'dark' ? '#131b2e' : '#ffffff',
          color: theme.palette.mode === 'dark' ? '#f8fafc' : '#0f172a',
          backgroundImage: 'none',
          border: isMobile ? 'none' : (theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'),
          boxShadow: isMobile ? 'none' : (theme.palette.mode === 'dark' ? '0 25px 50px rgba(0, 0, 0, 0.6)' : '0 20px 50px rgba(0, 0, 0, 0.15)'),
        }
      }}
    >
      <DialogTitle
        sx={{
          background: theme.palette.mode === 'dark' ? '#1e293b' : '#2d3484',
          color: 'white',
          py: 2,
          px: 2.5,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 100,
            height: 100,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            top: -20,
            right: -10,
            pointerEvents: 'none',
          }}
        />
        <Box display="flex" justifyContent="space-between" alignItems="center" position="relative" zIndex={1}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon>confirmation_number</Icon> Formulario de Reserva
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.12)', borderRadius: '6px' }}>
            <Icon sx={{ fontSize: 20 }}>close</Icon>
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: { xs: 2.5, sm: 3 },
          bgcolor: theme.palette.mode === 'dark' ? '#131b2e' : '#ffffff',
          color: theme.palette.mode === 'dark' ? '#f8fafc' : '#0f172a',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0',
        }}
      >
        {success ? (
          <Box textAlign="center" py={4}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'rgba(16, 185, 129, 0.15)',
                color: '#10b981',
                mx: 'auto',
                mb: 2,
                borderRadius: '8px',
              }}
            >
              <Icon sx={{ fontSize: 36 }}>check_circle</Icon>
            </Avatar>
            <Alert severity="success" sx={{ mb: 2, borderRadius: '8px', fontWeight: 700 }}>
              ¡Reserva procesada exitosamente!
            </Alert>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Se ha confirmado la reserva de tus boletos.
            </Typography>
            <Typography variant="caption" sx={{ mt: 1.5, display: 'block', color: theme.palette.text.secondary }}>
              El tablero se actualizará automáticamente...
            </Typography>
          </Box>
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
              <Form>
                {/* Boletos Seleccionados */}
                <Box mb={2.5}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: 800,
                      color: theme.palette.mode === 'dark' ? '#f8fafc' : '#1e293b',
                      mb: 1,
                    }}
                  >
                    <Icon sx={{ mr: 1, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, fontSize: 18 }}>confirmation_number</Icon>
                    Boletos Seleccionados ({numerosSeleccionados.length})
                  </Typography>
                  <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                    {numerosSeleccionados.map((numero) => (
                      <Chip
                        key={numero}
                        label={numero}
                        onDelete={() => handleRemoveNumber(numero)}
                        sx={{
                          bgcolor: '#e11d48',
                          color: 'white',
                          fontWeight: 800,
                          borderRadius: '6px',
                          '& .MuiChip-deleteIcon': {
                            color: 'rgba(255, 255, 255, 0.85)',
                            '&:hover': { color: 'white' },
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>

                {/* Datos del Comprador */}
                <Typography
                  variant="subtitle2"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 800,
                    color: theme.palette.mode === 'dark' ? '#f8fafc' : '#1e293b',
                    mb: 1,
                  }}
                >
                  <Icon sx={{ mr: 1, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, fontSize: 18 }}>person</Icon>
                  Datos del Comprador
                </Typography>

                {/* Nombre */}
                <TextField
                  fullWidth
                  size="small"
                  name="nombre"
                  label="Nombre Completo"
                  value={values.nombre}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.nombre && Boolean(errors.nombre)}
                  helperText={touched.nombre && errors.nombre}
                  margin="dense"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon sx={{ color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, fontSize: 20 }}>badge</Icon>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Teléfono con código de país */}
                <Box mt={1}>
                  <CampoTelefonoConCodigo
                    nombre="telefono"
                    etiqueta="Teléfono Móvil (WhatsApp)"
                    selectedCountry={selectedCountry}
                    setSelectedCountry={setSelectedCountry}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                    values={values}
                  />
                </Box>

                {/* Email */}
                <TextField
                  fullWidth
                  size="small"
                  name="email"
                  label="Correo Electrónico (Opcional)"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  margin="dense"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon sx={{ color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, fontSize: 20 }}>email</Icon>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mt: 1.5 }}
                />

                {/* Total */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    mt: 2.5,
                    p: 2,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.08)' : 'rgba(45, 52, 132, 0.05)',
                    borderRadius: '10px',
                    border: theme.palette.mode === 'dark' ? '1.5px solid rgba(99, 102, 241, 0.25)' : '1.5px solid rgba(45, 52, 132, 0.15)',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 800,
                      color: theme.palette.mode === 'dark' ? '#f8fafc' : '#1e293b',
                    }}
                  >
                    Total a Pagar ({numerosSeleccionados.length} {numerosSeleccionados.length === 1 ? 'boleto' : 'boletos'}):
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                      background: theme.palette.primary.main,
                      color: 'white',
                      px: 2.2,
                      py: 0.8,
                      borderRadius: '8px',
                      boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(45, 52, 132, 0.25)',
                    }}
                  >
                    <Icon sx={{ mr: 0.8, fontSize: 20 }}>payments</Icon>
                    <Typography variant="h6" sx={{ fontWeight: 900, fontSize: '1.15rem' }}>
                      $ {totalPagar}
                    </Typography>
                  </Box>
                </Box>

                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mt: 2,
                      borderRadius: '8px',
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {error}
                    </Typography>
                  </Alert>
                )}

                <DialogActions sx={{ px: 0, pt: 2.5, gap: 1.2 }}>
                  <Button
                    onClick={handleClose}
                    disabled={loading}
                    variant="outlined"
                    sx={{
                      fontWeight: 700,
                      borderRadius: '8px',
                      color: theme.palette.mode === 'dark' ? '#94a3b8' : '#f15b28',
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#f15b28',
                      '&:hover': {
                        borderColor: theme.palette.mode === 'dark' ? '#ffffff' : '#d84e1f',
                        color: theme.palette.mode === 'dark' ? '#ffffff' : '#d84e1f',
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(241, 91, 40, 0.08)',
                      },
                    }}
                  >
                    Cancelar
                  </Button>

                  <Button
                    type="submit"
                    variant={theme.palette.mode === 'dark' ? 'contained' : 'outlined'}
                    color="primary"
                    disabled={loading || isSubmitting}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Icon>check</Icon>}
                    sx={{
                      fontWeight: 800,
                      borderRadius: '8px',
                      borderWidth: theme.palette.mode === 'dark' ? '0px' : '1.5px',
                      borderColor: theme.palette.mode === 'dark' ? 'transparent' : '#2d3484',
                      color: theme.palette.mode === 'dark' ? '#ffffff' : '#2d3484',
                      '&:hover': {
                        borderWidth: theme.palette.mode === 'dark' ? '0px' : '1.5px',
                        borderColor: theme.palette.mode === 'dark' ? 'transparent' : '#1c215e',
                        bgcolor: theme.palette.mode === 'dark' ? '#373ea8' : 'rgba(45, 52, 132, 0.08)',
                      },
                    }}
                  >
                    {loading ? 'Procesando...' : 'Confirmar Reserva'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FormularioReserva;
